import { takeLatest, put, call } from 'redux-saga/effects';
import { message } from 'antd';
import * as actions from './actions';
import { ordersAPI } from '../../api/config';
import { PO_PENDING_APPROVAL } from '../../redux/constants/actionTypes';

// Utility function to download PDF blob
function downloadPDF(blob, headers, orderData = null) {
  try {
    // Try to get purchase order ID and vendor name from headers first
    let purchaseOrderId = headers['x-purchase-order-id'];
    let vendorName = headers['x-vendor-name'];
    
    console.log('Headers:', headers);
    console.log('Purchase Order ID from header:', purchaseOrderId);
    console.log('Vendor Name from header:', vendorName);
    
    // If orderData is provided and headers don't have the data, use orderData
    if (orderData) {
      if (!purchaseOrderId && orderData.id) {
        purchaseOrderId = orderData.id;
        console.log('Using Purchase Order ID from orderData:', purchaseOrderId);
      }
      if (!vendorName && orderData.vendor_name) {
        vendorName = orderData.vendor_name;
        console.log('Using Vendor Name from orderData:', vendorName);
      }
    }
    
    // Generate filename in PO_{id}_VendorName.pdf format
    let filename = 'purchase_order.pdf';
    
    if (purchaseOrderId) {
      const formattedId = String(purchaseOrderId).padStart(5, '0');
      const sanitizedVendorName = vendorName
        ? vendorName.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30)
        : 'Unknown';
      
      filename = `PO_${formattedId}_${sanitizedVendorName}.pdf`;
      console.log('Generated filename:', filename);
    } else {
      // Fallback: Extract filename from Content-Disposition header
      const contentDisposition = headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      console.log('Using fallback filename:', filename);
    }

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return {
      filename,
      purchaseOrderId: purchaseOrderId || headers['x-purchase-order-id'],
      totalAmount: headers['x-total-amount']
    };
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF');
  }
}

// Fetch Orders
function* fetchOrdersSaga(action) {
  try {
    const { page, limit } = action.payload;
    const response = yield call(ordersAPI.fetchOrders, page, limit);
    yield put(actions.fetchOrdersSuccess(response.data));
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to fetch orders';
    yield put(actions.fetchOrdersFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Fetch Order by ID
function* fetchOrderByIdSaga(action) {
  try {
    const id = action.payload;
    const response = yield call(ordersAPI.getOrder, id);
    yield put(actions.fetchOrderByIdSuccess(response.data));
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to fetch order details';
    yield put(actions.fetchOrderByIdFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Create Order
function* createOrderSaga(action) {
  try {
    // Transform payload to match new API format with items array
    const { items, ...orderData } = action.payload;
    
    // If items is not an array, assume single product format and convert to array
    const orderPayload = items && Array.isArray(items) ? {
      ...orderData,
      items: items
    } : {
      vendor_id: orderData.vendor_id,
      vendor_name: orderData.vendor_name,
      delivery_address: orderData.delivery_address,
      firm_name: orderData.firm_name,
      items: [{
        product_id: orderData.product_id,
        product_name: orderData.product_name,
        quantity: orderData.quantity,
        rate: orderData.rate
      }]
    };
    
    const response = yield call(ordersAPI.createOrder, orderPayload);

    // Handle PDF blob response
    if (response.data instanceof Blob) {
      // Check if the blob is actually a JSON response (e.g. pending_approval)
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Parse the blob as text → JSON
        const text = yield response.data.text();
        const jsonData = JSON.parse(text);
        if (jsonData.order_status === 'pending_approval') {
          // PO needs approval — do NOT download PDF, show approval pending modal
          yield put({ type: PO_PENDING_APPROVAL, payload: jsonData });
          yield put(actions.createOrderSuccess({ success: true, pendingApproval: true }));
          yield put(actions.fetchOrdersRequest(1, 10));
          return;
        }
        // Other JSON response
        yield put(actions.createOrderSuccess(jsonData));
        yield put(actions.fetchOrdersRequest(1, 10));
        return;
      }

      // Regular PDF download
      const downloadInfo = yield call(downloadPDF, response.data, response.headers, orderPayload);
      message.success(`Purchase order #${downloadInfo.purchaseOrderId} created successfully! PDF downloaded: ${downloadInfo.filename}`);
      yield put(actions.createOrderSuccess({
        success: true,
        message: 'Purchase order created and PDF downloaded successfully',
        filename: downloadInfo.filename,
        purchaseOrderId: downloadInfo.purchaseOrderId,
        totalAmount: downloadInfo.totalAmount
      }));
    } else {
      // Fallback for non-blob response (backward compatibility)
      if (response.data?.order_status === 'pending_approval') {
        yield put({ type: PO_PENDING_APPROVAL, payload: response.data });
        yield put(actions.createOrderSuccess({ success: true, pendingApproval: true }));
        yield put(actions.fetchOrdersRequest(1, 10));
        return;
      }
      yield put(actions.createOrderSuccess(response.data));
    }
    
    // Refresh the orders list after successful creation
    yield put(actions.fetchOrdersRequest(1, 10));
  } catch (error) {
    message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to create order');
    yield put(actions.createOrderFailure(error.response?.data?.error || error.response?.data?.message || 'Failed to create order'));
  }
}

// Update Order
function* updateOrderSaga(action) {
  try {
    const { id, orderData } = action.payload;
    const response = yield call(ordersAPI.updateOrder, id, orderData);
    yield put(actions.updateOrderSuccess(response.data));
    message.success(response.data.message || 'Purchase order updated successfully');
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update order';
    yield put(actions.updateOrderFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Redownload Order
function* redownloadOrderSaga(action) {
  try {
    const orderId = action.payload;
    const response = yield call(ordersAPI.redownloadOrder, orderId);
    
    // Handle PDF blob response
    if (response.data instanceof Blob) {
      // Download the PDF automatically
      const downloadInfo = yield call(downloadPDF, response.data, response.headers);
      
      // Show success message
      message.success(`Purchase order #${downloadInfo.purchaseOrderId} PDF downloaded: ${downloadInfo.filename}`);
      
      // Dispatch success with download information
      yield put(actions.redownloadOrderSuccess({
        success: true,
        message: 'Purchase order PDF downloaded successfully',
        filename: downloadInfo.filename,
        purchaseOrderId: downloadInfo.purchaseOrderId,
        totalAmount: downloadInfo.totalAmount
      }));
    } else {
      yield put(actions.redownloadOrderSuccess(response.data));
    }
  } catch (error) {
    message.error('Failed to download purchase order PDF');
    yield put(actions.redownloadOrderFailure(error.response?.data?.error || error.response?.data?.message || 'Failed to redownload order'));
  }
}


// Accept Short Delivery - closes a PO line item at less than the ordered
// quantity, shrinking its ordered quantity to match what was delivered.
function* acceptShortfallSaga(action) {
  try {
    const { poId, itemId, reason, page } = action.payload;
    const response = yield call(ordersAPI.acceptShortfall, poId, itemId, { reason });
    yield put(actions.acceptShortfallSuccess(response.data));
    message.success(response.data.message || 'Shortfall accepted; purchase order line closed.');
    yield put(actions.fetchOrdersRequest(page, 10));
  } catch (error) {
    const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to accept short delivery';
    yield put(actions.acceptShortfallFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Watch Orders
export function* watchOrders() {
  yield takeLatest(actions.FETCH_ORDERS_REQUEST, fetchOrdersSaga);
  yield takeLatest(actions.FETCH_ORDER_BY_ID_REQUEST, fetchOrderByIdSaga);
  yield takeLatest(actions.CREATE_ORDER_REQUEST, createOrderSaga);
  yield takeLatest(actions.UPDATE_ORDER_REQUEST, updateOrderSaga);
  yield takeLatest(actions.REDOWNLOAD_ORDER_REQUEST, redownloadOrderSaga);
  yield takeLatest(actions.ACCEPT_SHORTFALL_REQUEST, acceptShortfallSaga);
}
